import { z } from 'zod';

export const divisionResSchema = z.object({
  id: z.string().nullable(),
  divCode: z.string().nullable(),
  displayName: z.string().nullable()
});
export type DivisionRes = z.infer<typeof divisionResSchema>;

export const bodyPartResSchema = z.object({
  id: z.string().nullable(),
  bodyPartCode: z.string().nullable(),
  displayName: z.string().nullable(),
  bodyPartCodeI18n: z.string().nullable()
});
export type BodyPartRes = z.infer<typeof bodyPartResSchema>;

export const imageTagResSchema = z.object({
  id: z.string().nullable(),
  tagName: z.string().nullable()
});
export type ImageTagRes = z.infer<typeof imageTagResSchema>;

export const pageInfoResSchema = z.object({
  hasPrePage: z.boolean().nullable(),
  hasNextPage: z.boolean().nullable(),
  cursor: z.string().nullable()
});
export type PageInfoRes = z.infer<typeof pageInfoResSchema>;

export const imageInfoResSchema = z.object({
  id: z.string().nullable(),
  imageName: z.string().nullable(),
  imageTagResList: z.array(imageTagResSchema).nullable(),
  bodyPartResList: z.array(bodyPartResSchema).nullable(),
  divisionResList: z.array(divisionResSchema).nullable(),
  isCommonUsed: z.boolean().nullable(),
  createUser: z.string().nullable(),
  createUserName: z.string().nullable(),
  modifyUser: z.string().nullable(),
  createDatetime: z.string().nullable(),
  modifyDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type ImageInfoRes = z.infer<typeof imageInfoResSchema>;

export const jsonNodeSchema = z.unknown();
export type JsonNode = z.infer<typeof jsonNodeSchema>;

export const patientAndEncounterResSchema = z.object({
  patientName: z.string().nullable(),
  mrn: z.string().nullable(),
  idNo: z.string().nullable(),
  birthDate: z.string().nullable(),
  gender: z.string().nullable(),
  attendingDoctor: z.string().nullable(),
  admissionDateTime: z.string().nullable(),
  admissionIcdCmName: z.string().nullable(),
  patientSource: z.string().nullable(),
  bedNo: z.string().nullable(),
  createUser: z.string().nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable()
});
export type PatientAndEncounterRes = z.infer<typeof patientAndEncounterResSchema>;

export const imageInfoPageResSchema = z.object({
  imageInfoResList: z.array(imageInfoResSchema).nullable(),
  pageInfoRes: pageInfoResSchema.nullable()
});
export type ImageInfoPageRes = z.infer<typeof imageInfoPageResSchema>;

export const tagInfoResSchema = z.object({
  id: z.string().nullable(),
  tagName: z.string().nullable()
});
export type TagInfoRes = z.infer<typeof tagInfoResSchema>;

export const patientImageEditResSchema = z.object({
  id: z.string().nullable(),
  mrn: z.string().nullable(),
  imageId: z.string().nullable(),
  editData: jsonNodeSchema.nullable()
});
export type PatientImageEditRes = z.infer<typeof patientImageEditResSchema>;

export const apiResponseListBodyPartResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(bodyPartResSchema).nullable()
});
export type ApiResponseListBodyPartRes = z.infer<typeof apiResponseListBodyPartResSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseListDivisionResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(divisionResSchema).nullable()
});
export type ApiResponseListDivisionRes = z.infer<typeof apiResponseListDivisionResSchema>;

export const apiResponsePatientAndEncounterResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: patientAndEncounterResSchema.nullable()
});
export type ApiResponsePatientAndEncounterRes = z.infer<typeof apiResponsePatientAndEncounterResSchema>;

export const sseEmitterSchema = z.object({
  timeout: z.number().nullable()
});
export type SseEmitter = z.infer<typeof sseEmitterSchema>;

export const apiResponseImageInfoPageResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: imageInfoPageResSchema.nullable()
});
export type ApiResponseImageInfoPageRes = z.infer<typeof apiResponseImageInfoPageResSchema>;

export const apiResponseListTagInfoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(tagInfoResSchema).nullable()
});
export type ApiResponseListTagInfoRes = z.infer<typeof apiResponseListTagInfoResSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const apiResponseListPatientImageEditResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(patientImageEditResSchema).nullable()
});
export type ApiResponseListPatientImageEditRes = z.infer<typeof apiResponseListPatientImageEditResSchema>;

export const patientImageEditReqSchema = z.object({
  mrn: z.string().min(1),
  imageId: z.string().min(1),
  editData: z.preprocess((val) => val === undefined ? null : val, jsonNodeSchema.nullish())
});
export type PatientImageEditReq = z.infer<typeof patientImageEditReqSchema>;

export const uploadAndAddImagesInfoReqSchema = z.object({
  imageName: z.string().min(1),
  tags: z.array(z.string()).nullable(),
  bodyParts: z.array(z.string()).nullable(),
  divCodes: z.array(z.string()).nullable()
});
export type UploadAndAddImagesInfoReq = z.infer<typeof uploadAndAddImagesInfoReqSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const apiResponseListImageInfoResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(imageInfoResSchema).nullable()
});
export type ApiResponseListImageInfoRes = z.infer<typeof apiResponseListImageInfoResSchema>;

export const imageInfoReqSchema = z.object({
  id: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  imageName: z.string().min(1),
  tags: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish()),
  bodyParts: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish()),
  divCodes: z.preprocess((val) => val === undefined ? null : val, z.array(z.string()).nullish()),
  lockVersion: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type ImageInfoReq = z.infer<typeof imageInfoReqSchema>;

// Query Parameter Schemas

export const updateUserCommonTagQuerySchema = z.object({
  tagId: z.string()
});
export type UpdateUserCommonTagQuery = z.infer<typeof updateUserCommonTagQuerySchema>;

export const queryImagesInfoByBodyPartCodeAndSearchQuerySchema = z.object({
  bodyPartCode: z.string().nullish(),
  divCode: z.string().nullish(),
  tagNameList: z.array(z.string()).nullish(),
  search: z.string().nullish(),
  cursor: z.string().nullish(),
  isNext: z.boolean().nullish(),
  pageSize: z.number().nullish()
});
export type QueryImagesInfoByBodyPartCodeAndSearchQuery = z.infer<typeof queryImagesInfoByBodyPartCodeAndSearchQuerySchema>;

export const downloadFileQuerySchema = z.object({
  fileId: z.string()
});
export type DownloadFileQuery = z.infer<typeof downloadFileQuerySchema>;

export const queryPatientAndEncounterByMrnQuerySchema = z.object({
  mrn: z.string(),
  encounterId: z.string()
});
export type QueryPatientAndEncounterByMrnQuery = z.infer<typeof queryPatientAndEncounterByMrnQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
